import { z } from 'zod';
/**
 * Showcase print and email sets (docs/adr/0072-showcase-print-and-email.md, SC-19): the page size, layout and proof
 * margins of a print set, the print-ready PDF of an approved version, and the conversions both apps use. Kept apart
 * from showcase.ts so the screen phases and the print phase do not edit the same lines.
 */
/** Page sizes a print set can start from (trim size, portrait, in millimetres). */
export const PRINT_PAGE_SIZES = {
    a4: { label: 'A4', widthMm: 210, heightMm: 297 },
    a5: { label: 'A5', widthMm: 148, heightMm: 210 },
    dl: { label: 'DL', widthMm: 99, heightMm: 210 },
};
export const PRINT_PAGE_PRESETS = Object.keys(PRINT_PAGE_SIZES);
/** `single`: one page per PDF page, shown two at a time as in a printed brochure. `spreads`: each PDF page is a spread. */
export const PrintLayout = z.enum(['single', 'spreads']);
export const PrintOrientation = z.enum(['portrait', 'landscape']);
export const PRINT_TRIM_MIN_MM = 60;
export const PRINT_TRIM_MAX_MM = 1000;
export const PRINT_BLEED_MAX_MM = 10;
export const PRINT_SAFE_MAX_MM = 30;
/** Usual defaults for commercial print. */
export const DEFAULT_BLEED_MM = 3;
export const DEFAULT_SAFE_MM = 5;
/** Email designs (EDMs) are shown at this width as one long scroll. */
export const EMAIL_WIDTH = 600;
/** A print set's page: its trim size (one page, as printed), layout and the proof margins. */
export const PrintSpec = z.object({
    layout: PrintLayout,
    orientation: PrintOrientation,
    trimWidthMm: z.number(),
    trimHeightMm: z.number(),
    bleedMm: z.number(),
    safeMm: z.number(),
});
const Mm = (min, max) => z.number().min(min).max(max).multipleOf(0.5, 'Use whole or half millimetres');
const TrimMm = Mm(PRINT_TRIM_MIN_MM, PRINT_TRIM_MAX_MM);
/** When a print set is made. A custom page size gives its trim in millimetres; a preset uses its own. */
export const PrintSpecInput = z.object({
    layout: PrintLayout.default('single'),
    orientation: PrintOrientation.default('portrait'),
    trimWidthMm: TrimMm.optional(),
    trimHeightMm: TrimMm.optional(),
    bleedMm: Mm(0, PRINT_BLEED_MAX_MM).default(DEFAULT_BLEED_MM),
    safeMm: Mm(0, PRINT_SAFE_MAX_MM).default(DEFAULT_SAFE_MM),
});
/** Later changes: the layout and the proof margins (the page size is chosen when the set is made). */
export const UpdatePrintSpecInput = z
    .object({ layout: PrintLayout.optional(), bleedMm: Mm(0, PRINT_BLEED_MAX_MM).optional(), safeMm: Mm(0, PRINT_SAFE_MAX_MM).optional() })
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change' });
export const mmToPx = (mm) => (mm / 25.4) * 96;
export const ptToMm = (pt) => (pt * 25.4) / 72;
export const mmToPt = (mm) => (mm / 25.4) * 72;
/** Millimetres to one decimal place, as people read them. */
export const roundMm = (mm) => Math.round(mm * 10) / 10;
/** A print set's trim (one page) from a preset and orientation, or the custom size given. Null when custom lacks a size. */
export function printTrimMm(preset, input) {
    const size = PRINT_PAGE_SIZES[preset];
    let w;
    let h;
    if (size) {
        w = size.widthMm;
        h = size.heightMm;
    }
    else {
        if (input.trimWidthMm === undefined || input.trimHeightMm === undefined)
            return null;
        w = input.trimWidthMm;
        h = input.trimHeightMm;
    }
    const landscape = (input.orientation ?? 'portrait') === 'landscape';
    // A preset is portrait as listed; landscape turns it. A custom size is taken as given.
    if (size && landscape)
        return { width: h, height: w };
    return { width: w, height: h };
}
/**
 * The device size (CSS pixels at 96 dpi) a print set is drawn at: one uploaded page, which is a spread of two pages in
 * the spreads layout. Scaled to fit the device limits, keeping its shape.
 */
export function printDevicePx(trim, layout, limits = { min: 200, max: 4000 }) {
    let w = mmToPx(trim.width * (layout === 'spreads' ? 2 : 1));
    let h = mmToPx(trim.height);
    const down = Math.min(1, limits.max / Math.max(w, h));
    w *= down;
    h *= down;
    const up = Math.max(1, limits.min / Math.min(w, h));
    w = Math.min(limits.max, w * up);
    h = Math.min(limits.max, h * up);
    return { width: Math.round(w), height: Math.round(h) };
}
/** "A4 portrait", "DL", "Custom 120 × 180 mm", with " · spreads" for the spreads layout. */
export function printSizeLabel(preset, spec) {
    const size = PRINT_PAGE_SIZES[preset];
    const base = size ? `${size.label} ${spec.orientation}` : `Custom ${roundMm(spec.trimWidthMm)} × ${roundMm(spec.trimHeightMm)} mm`;
    return spec.layout === 'spreads' ? `${base} · spreads` : base;
}
/** The print-ready PDF of the approved version: a 5-minute signed link, served as an attachment. */
export const DesignPrintPdf = z.object({
    url: z.string(),
    fileName: z.string(),
    size: z.number().int(),
    pages: z.number().int(),
    versionNumber: z.number().int(),
    urlsExpireAt: z.string(),
});
const def = (r) => r;
const id = (name) => z.object({ [name]: z.uuid() });
export const showcasePrintRoutes = {
    /**
     * The print-ready PDF of a print set's approved version (the latest one clients see): the approved pages in order,
     * from the original PDFs. The team, and clients when the set allows downloads. 409 until the version is approved.
     */
    getDesignPrintPdf: def({ showcaseGuest: true, rateLimit: 'uploads',
        method: 'GET', path: '/v1/showcase/sets/:setId/print-pdf', params: id('setId'), response: DesignPrintPdf,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase',
    }),
};
//# sourceMappingURL=showcase-print.js.map